import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    // Two independent slideshow-component instances (this one, and the zoom dialog's own) need
    // to keep `current` in sync bidirectionally - selecting/dragging/swiping in either should
    // update the other to match. Each side listens for the OTHER's SlideshowSelectEvent and
    // replays it via select(). No explicit bounce-guard is needed: select() on a clone-loop
    // slideshow (both instances use clone_loop now) no-ops once the target index already matches
    // `current` (see #performCloneLoopSelect's `targetSlide === currentSlide` check in
    // slideshow.js), so a sync-triggered select() naturally terminates the round-trip after one
    // hop without any extra state to track.
    //
    // Each side also skips relaying while the TARGET's own gesture is still active
    // (`gestureActive` - true while a pointer is down OR the scroll is still actively moving,
    // e.g. native momentum/inertial scroll continuing after the finger already lifted) - live-
    // tested and confirmed a real bug without this: a sync echo arriving while the user was
    // actively swiping (or that swipe's momentum was still gliding) on the OTHER instance could
    // yank that instance's scroll position/`current` back to a stale value, fighting the in-
    // progress gesture (observed as "can't swipe past a certain slide" - every attempt got
    // interrupted, including ones where the finger had already lifted but momentum was still
    // carrying the scroll). Skipping (not queuing) is correct here, not a gap: the gesture that's
    // actually happening will produce its own fresh SlideshowSelectEvent once it genuinely
    // settles, which syncs correctly on its own.
    //
    // The zoom-dialog side listens on `zoomDialogComponent` itself (not its nested
    // `refs.zoomSlideshow`): custom elements upgrade/connect in tree order, so this element's
    // own connectedCallback can run before the nested <zoom-dialog>'s does, leaving its `refs`
    // still empty at this point. SlideshowSelectEvent bubbles, so listening on the ancestor
    // `zoomDialogComponent` element (which exists synchronously regardless of its own
    // connectedCallback timing) reliably catches it once it's dispatched, without that race.
    this.refs.slideshow?.addEventListener(
      SlideshowSelectEvent.eventName,
      (event) => {
        const zoomSlideshow = this.refs.zoomDialogComponent?.refs.zoomSlideshow;
        if (!zoomSlideshow || zoomSlideshow.gestureActive) return;
        zoomSlideshow.select(event.detail.index, undefined, { animate: false });
      },
      { signal }
    );
    this.refs.zoomDialogComponent?.addEventListener(
      SlideshowSelectEvent.eventName,
      (event) => {
        const slideshow = this.refs.slideshow;
        if (!slideshow || slideshow.gestureActive) return;

        // In `hero` presentation, the main gallery's own `slideshow-component` (the mobile
        // carousel) is `display: none` on desktop viewports (see
        // product-media-gallery-content-styles.liquid - a separate, static hero image +
        // thumbnails is shown there instead; the zoom dialog's OWN slideshow stays visible
        // and draggable regardless). A `display: none` slideshow has zero-size slide
        // geometry, so `select()`'s scroll-to-target has nothing meaningful to scroll to -
        // live-tested and confirmed a real bug without this check: the scroll silently
        // landed back at its already-zero position instead of the requested index,
        // `current` got set to whatever (unrelated) slide happened to sit at that
        // collapsed position, and THAT wrong state then synced back into the zoom dialog
        // via its own SlideshowSelectEvent - visibly yanking the just-opened zoomed image
        // back to a different one right after a thumbnail click. `offsetParent === null` is
        // the standard cheap check for "not actually rendered" (covers `display: none` on
        // the element itself or any ancestor, robust regardless of which specific rule is
        // hiding it) - skip the sync entirely in that case, since there's truly nothing to
        // keep in sync while it's not shown; the next time it becomes visible (e.g. the
        // viewport crosses back below the hero breakpoint), its OWN reconnect/resize
        // handling re-establishes correct geometry independently of this sync path.
        if (slideshow.offsetParent === null) return;

        slideshow.select(event.detail.index, undefined, { animate: false });
      },
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Preload-before-swap safety timeout (ms) - see spec design §4. A slow or broken image
   * must not block the variant swap indefinitely; swap anyway past this point.
   * @type {number}
   */
  static #PRELOAD_SAFETY_TIMEOUT_MS = 400;

  /**
   * Monotonic counter, incremented once per `#handleProductSelect` invocation - lets a
   * resolving call recognize it's been superseded by a newer one (see the guard in
   * `#handleProductSelect` below) rather than trusting `this.isConnected` alone, which
   * stays true for the ENTIRE window between two rapid color changes (both fire before
   * either's `replaceWith()` has run), so it can't by itself distinguish "still the
   * current, active request" from "a newer request has since been dispatched, this one is
   * now stale".
   * @type {number}
   */
  #productSelectRequestId = 0;

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * A rapid second Farbe change can dispatch a new `ProductSelectEvent` before this
   * handler's own async preload-before-swap wait (`#waitForFirstImageReady`, up to
   * `#PRELOAD_SAFETY_TIMEOUT_MS`) has resolved for the first one: without the request-id
   * guard below, whichever of the two resolves LAST wins the `replaceWith()`, which could
   * be the older, now-stale color if its image happened to decode faster or its safety
   * timeout landed later than the newer request's. The guard re-checks
   * `#productSelectRequestId` immediately before swapping, discarding the result of any
   * call that's since been superseded.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const requestId = ++this.#productSelectRequestId;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        await MediaGallery.#waitForFirstImageReady(newMediaGallery);

        // A newer Farbe change may have dispatched (and possibly already resolved) its own
        // ProductSelectEvent while this one was awaiting its preload above - bail rather
        // than swap in a now-stale color's gallery over a newer one.
        if (requestId !== this.#productSelectRequestId) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Resolves once the KEY images of a freshly-fetched (not yet inserted) media
   * gallery have decoded, or after a short safety timeout — whichever comes
   * first. "Key" = the first visible slide's `<img>` AND the last slide's `<img>`.
   *
   * Root cause of the variant-change white-flash (spec design §4): swapping in
   * brand-new `<img>` DOM nodes with no cached decode leaves nothing to paint
   * until fetch+decode completes (1–2s observed). The first slide is what shows
   * on swap; the last slide is the product shot (Freisteller / Kissen photo —
   * `product-media-gallery-content.liquid` orders mood images first, the
   * selected variant's `featured_media` last), which the shopper reveals via a
   * thumbnail or scroll moments later and which was otherwise always cold. See
   * docs/superpowers/specs/2026-09-08-configurator-kissen-gallery-image-design.md
   * "Post-QA fixes (v5) — G".
   *
   * No `fetchpriority="high"` — a same-session smoothing measure, not a
   * page-load LCP candidate. The 400ms cap is unchanged: a slow connection just
   * swaps a touch earlier with an image still warming, no worse than before.
   * @param {Element} newMediaGallery - Parsed but not-yet-connected <media-gallery>.
   * @returns {Promise<void>}
   */
  static #waitForFirstImageReady(newMediaGallery) {
    // The mobile carousel's own slides carry the full ordered media set (mood
    // first, product shot last). Use those for first + last. Only when there are
    // no such slides (a gallery shape with just the hero) fall back to the hero
    // image — matching today's single-`querySelector` behaviour for that case.
    let slideImages = newMediaGallery.querySelectorAll('slideshow-slide:not([hidden]) img');
    if (slideImages.length === 0) {
      slideImages = newMediaGallery.querySelectorAll('.media-gallery__hero img');
    }
    const first = slideImages[0];
    const last = slideImages[slideImages.length - 1];

    const decodePromises = [];
    for (const img of new Set([first, last])) {
      const p = MediaGallery.#decodeDetached(img);
      if (p) decodePromises.push(p);
    }
    if (decodePromises.length === 0) return Promise.resolve();

    const timeoutPromise = new Promise((resolve) => {
      setTimeout(resolve, MediaGallery.#PRELOAD_SAFETY_TIMEOUT_MS);
    });

    return Promise.race([Promise.all(decodePromises), timeoutPromise]);
  }

  /**
   * Kicks a real `Image()` fetch+decode for one `<img>` from a DOMParser'd (never
   * fetching) gallery and returns a promise that resolves when it decodes (or
   * rejects-swallowed). `null` when `img` isn't an image / has no src.
   *
   * A `mobile_only`-rendered slide wraps its `<img>` in `<picture><source media>`
   * with the real `srcset`/`sizes` on the `<source>` and a deliberately src-less
   * `<img>` — prefer the enclosing `<source>` (same reasoning as slideshow.js's
   * `#buildBoundaryClone()`). Browsers cache/dedupe by resolved URL, so once this
   * detached `Image()` finishes, the identical `<img>` that lands via
   * `replaceWith()` paints from cache — the actual "preload before swap".
   * @param {Element | undefined} img
   * @returns {Promise<void> | null}
   */
  static #decodeDetached(img) {
    if (!(img instanceof HTMLImageElement)) return null;

    const source = img.closest('picture')?.querySelector('source');
    const srcset = source?.getAttribute('srcset') || img.getAttribute('srcset') || '';
    const sizes = source?.getAttribute('sizes') || img.getAttribute('sizes') || '';
    const src = img.getAttribute('src') || '';
    if (!src && !srcset) return null;

    const preloadImage = new Image();
    if (sizes) preloadImage.sizes = sizes;
    if (srcset) preloadImage.srcset = srcset;
    if (src) preloadImage.src = src;

    return preloadImage.decode().catch(() => {
      // decode() rejects if the image errors, or in some browsers if it's not yet
      // connected to the document — fall through rather than reject the outer wait.
    });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
